import type { FilterGroupV1Option } from '@/features/filter/filter-data-v1-type';

import React from 'react';

import chunk from 'lodash.chunk';

import { IconImage } from '@/core/features';
import color from '@/core/features/styles/color';
import { getLastElement } from '@/core/utils/array';
import useFilterChip from '@/features/filter/filter-chip/use-filter-chip';

export default function FilterButton({
    columns,
    filterOption,
    index,
    isLoading,
    onFilterChipClick,
    optionCount,
    showDefaultSelected,
}: {
    columns: number;
    filterOption: FilterGroupV1Option;
    index: number;
    isLoading: boolean;
    onFilterChipClick: (option: FilterGroupV1Option) => void;
    optionCount: number;
    showDefaultSelected: boolean;
}) {
    const { count, isActive, onClick, title } = useFilterChip(
        filterOption,
        'multiple',
        showDefaultSelected,
        isLoading,
        onFilterChipClick,
    );

    const chunks = chunk([...Array(optionCount).keys()], columns);
    const borderTopLeft = hasTopLeftBorderElement(chunks, index);
    const borderTopRight = hasTopRightBorderElement(chunks, columns, index);
    const borderBottomLeft = hasBottomLeftBorderElement(chunks, columns, index);
    const borderBottomRight = hasBottomRightBorderElement(chunks, columns, index);

    return (
        <div
            className={`filterButton relative flex flex-col justify-center items-center w-full cursor-pointer select-none ${
                borderBottomLeft ? 'filterButton--borderBottomLeft' : ''
            } ${borderBottomRight ? 'filterButton--borderBottomRight' : ''} ${
                borderTopLeft ? 'filterButton--borderTopLeft' : ''
            } ${borderTopRight ? 'filterButton--borderTopRight' : ''} ${isActive ? 'filterButton--isActive' : ''}`}
            onClick={() => onClick()}
        >
            {filterOption.iconUrl && (
                <IconImage
                    className={'flex justify-center items-center'}
                    height={23}
                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                    style={{ marginBottom: 9 }}
                    url={filterOption.iconUrl}
                    width={23}
                />
            )}
            <div
                className={'text-12'}
                style={{ marginTop: filterOption.isDefault ? 20 : undefined }}
            >
                {title}
            </div>
            {!filterOption.isDefault && <div className={`text-10 ${count === null ? 'opacity-0' : ''}`}>({count})</div>}
            <style jsx>{`
                .filterButton {
                    height: 79px;
                    background-size: cover;
                }
                .filterButton:before {
                    content: '';
                    border: 1px solid ${color('alto')};
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: -1px;
                    left: -1px;
                }
                .filterButton:hover {
                    background-color: ${color('foam')};
                }
                .filterButton--isActive {
                    z-index: 1;
                    background-color: ${color('foam')};
                }
                .filterButton--isActive:before {
                    border-color: ${color('endeavour')};
                }
                .filterButton--borderTopLeft,
                .filterButton--borderTopLeft:before {
                    border-top-left-radius: 3px;
                }
                .filterButton--borderTopRight,
                .filterButton--borderTopRight:before {
                    border-top-right-radius: 3px;
                }
                .filterButton--borderBottomLeft,
                .filterButton--borderBottomLeft:before {
                    border-bottom-left-radius: 3px;
                }
                .filterButton--borderBottomRight,
                .filterButton--borderBottomRight:before {
                    border-bottom-right-radius: 3px;
                }
            `}</style>
        </div>
    );
}

const hasTopLeftBorderElement = (chunks: number[][], index: number): boolean => {
    return chunks[0]?.[0] === index;
};

const hasTopRightBorderElement = (chunks: number[][], columns: number, index: number): boolean => {
    const firstChunk = chunks[0] ?? [];

    switch (columns) {
        case 2:
            if (firstChunk.length > 1) {
                return firstChunk[1] === index;
            }
            return firstChunk[0] === index;
        case 3:
            if (firstChunk.length > 1) {
                return getLastElement(firstChunk) === index;
            }
            return firstChunk[0] === index;
        default:
            return firstChunk[0] === index;
    }
};

const hasBottomLeftBorderElement = (chunks: number[][], columns: number, index: number): boolean => {
    const lastChunk = getLastElement(chunks) ?? [];
    const lastChunkElement = getLastElement(lastChunk);

    switch (columns) {
        case 2:
            if (lastChunk.length > 1) {
                return lastChunk[0] === index;
            }
            return lastChunkElement === index;
        case 3:
            return lastChunk[0] === index;
        default:
            return lastChunkElement === index;
    }
};

const hasBottomRightBorderElement = (chunks: number[][], columns: number, index: number): boolean => {
    const lastChunk = getLastElement(chunks) ?? [];
    const lastChunkElement = getLastElement(lastChunk);
    switch (columns) {
        case 2:
            if (lastChunk.length > 1) {
                return lastChunkElement === index;
            }
            return false;
        case 3:
            if (lastChunk.length === 3) {
                return lastChunkElement === index;
            }
            return false;
        default:
            return lastChunkElement === index;
    }
};
